import { ApolloClient } from '@apollo/client'
import { datadogLogs } from '@datadog/browser-logs'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../../data/gql/questionnaire/queries'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../data/gql/questionnaire/queries/audiences'
import { GET_FIELDWORK } from '../../data/gql/questionnaire/queries/fieldwork'
import { GET_COUNTRIES_V2 } from '../../data/gql/questionnaire/queries/getCountries'
import {
  GET_MARKET_AUDIENCES,
  MARKET_AUDIENCES_CONTEXT,
  MarketAudiencesData
} from '../../data/gql/questionnaire/queries/marketAudiences'
import {
  AudienceResponseOption,
  DraftCustomAudience,
  FieldworkAudience,
  FieldworkAudienceType
} from '../../data/model/audience'
import {
  DraftQuestionItem,
  DraftQuestionnaireEntry
} from '../../data/model/questionnaire'
import { projectTypeHasMarkets } from '../../modules/Project/Project.hooks'
import {
  PROJECT_BY_SURVEY_ID,
  ProjectData
} from '../../modules/Project/Project.query'
import { formatMarketLine } from '../../modules/Questionnaire/MarketVisibility/useHiddenMarketsLabel'
import { MarketOption } from '../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { ResponseOptionsByQuestion } from '../../modules/Questionnaire/Questionnaire.slice'
import {
  flattenAllEntries,
  getResponseOptionsByQuestion
} from '../../modules/Questionnaire/Questionnaire.utils'
import { toMarketAudience } from '../../modules/Questionnaire/audienceDisplayLogic'
import { emptyMarketAudience } from '../../modules/Questionnaire/marketAudienceContext'
import { resolveLocaleOverrides } from '../../modules/Translations/lib/localeOverrides'
import { applyTranslationOverlay } from '../../modules/Translations/lib/previewOverlay'
import { createPreviewUrl } from '../HelperFunctions'
import { PanelSupplier, convertAudienceType } from '../audienceHelper'
import { fieldworkLanguages } from '../fieldworkLanguages'
import { BuildSurveyDocParams } from './buildSurveyDoc'
import {
  MarketExportMeta,
  PreviewLink,
  computeExportMarkets
} from './exportMarkets'

const DEFULT_COUNTRY_NAME = 'UK'

export interface LoadSurveyDocDataArgs {
  projectId: string
  surveyId: string
  waveId?: string
  languageCode?: string
  // The project's markets as resolved by `useProjectMarkets` (base market
  // first, then alphabetical). Used for rendering only — the multi-market
  // decision is taken from the freshly queried project type.
  allMarkets: MarketOption[]
  includeAudienceQuestions?: boolean
  // Market-specific export: every per-survey query runs against this market's
  // survey (its country, screeners and preview link), its screeners are
  // included, and the doc is filtered to what the market shows.
  market?: MarketOption
  // Strict mode: rethrow any failed fetch instead of shipping a partial (or
  // empty) doc. Off by default so existing callers keep their lenient export.
  throwOnError?: boolean
}

/**
 * Fetches everything `buildSurveyDoc` needs for one survey export (master or
 * one language). Plain async function so it can run outside React, e.g. once
 * per document for "Download all".
 */
export const loadSurveyDocData = async (
  client: ApolloClient<object>,
  {
    projectId,
    surveyId,
    waveId,
    languageCode,
    allMarkets,
    includeAudienceQuestions: includeAudienceQuestionsOption,
    market,
    throwOnError = false
  }: LoadSurveyDocDataArgs
): Promise<BuildSurveyDocParams> => {
  const { data: dataProject } = await client.query<ProjectData>({
    context: { clientName: 'dashboard' },
    query: PROJECT_BY_SURVEY_ID,
    variables: {
      surveyId
    }
  })

  const projectName = dataProject.project.name
  // Authoritative, load-aware multi-market check: read the project type from
  // the freshly-queried project (the same signal the rest of the app uses)
  // rather than inferring it from the render-time `allMarkets` hook, which
  // can be empty on a cold cache and would silently downgrade a multi-market
  // export to a single-market doc (wrong country, screener wrongly included,
  // no visibility annotations — with no error). A tracker wave exports
  // like a multi-market project, over the wave's own markets.
  const isMultiMarket = projectTypeHasMarkets(dataProject.project.projectType)

  // Multi-market master exports ship the shared questionnaire without the
  // per-market screener by default (audience questions differ per market);
  // callers may still override explicitly.
  // A market doc is one market's survey, so its own screener belongs in it.
  const includeAudienceQuestions =
    includeAudienceQuestionsOption ?? (!!market || !isMultiMarket)
  // The survey the per-survey queries run against: the market's own in a
  // market export. `draftQuestionnaireV2` returns the project-level `entries`
  // for any market's survey id, plus that market's `audienceEntries`.
  const docSurveyId = market?.surveyId ?? surveyId
  // Fieldwork codes can carry a region ("de-DE") while translations are
  // stored against the bare language ("de").
  const overlayLanguage = languageCode?.split('-')[0]
  // Market docs resolve screener text like survey text: the document's
  // language, else the stored main text. A local screener's main text can be
  // the market's own language, so the master (English) doc needs the `en`
  // translation applied too.
  const audienceOverlayLanguage = market
    ? overlayLanguage ?? 'en'
    : overlayLanguage
  let questionnaireEntries = [] as DraftQuestionnaireEntry[]
  let questionnaireAudienceEntries = [] as DraftQuestionnaireEntry[]
  let countryName = DEFULT_COUNTRY_NAME
  let panelSupplierCode = PanelSupplier.CUSTOM
  // A market doc shows its country's own wording (es-MX, en-US, fr-CA) where
  // one is stored, as respondents there get it. Master and language docs have
  // no country and keep the shared rows: language docs are re-imported.
  let localeCountry: string | undefined

  // try to get country name
  try {
    const { data: fieldworkData } = await client.query({
      context: { clientName: 'questionnaire' },
      query: GET_FIELDWORK,
      variables: {
        surveyId: docSurveyId
      }
    })

    const fieldwork = fieldworkData.fieldwork

    if (fieldwork) {
      if (market) localeCountry = fieldwork.locale.country
      panelSupplierCode = convertAudienceType(fieldwork.audience[0].__typename)

      const { data: dataCountries } = await client.query({
        context: { clientName: 'questionnaire' },
        query: GET_COUNTRIES_V2,
        variables: { panelSupplierCode }
      })

      countryName =
        dataCountries.getCountriesV2?.find((country) => {
          return country.countryCode === fieldwork.locale.country
        })?.name ?? DEFULT_COUNTRY_NAME
    }
  } catch (e) {
    if (throwOnError) throw e
    // leave countryName unchanged
  }
  if (market && !localeCountry) {
    datadogLogs.logger.warn(
      'Survey export: market doc has no country; its country-specific wording is not applied',
      { surveyId: docSurveyId }
    )
  }

  // Multi-market: the export runs off the master survey, so the single
  // `previewUrl` below points at the base market only. Resolve each market's
  // own language(s) and panel supplier from its fieldwork, then scope the
  // doc to the exported language (Canada + France for French, not the UK
  // English base) and emit one preview link per in-scope market. An explicit
  // market takes precedence over language-level scoping, including master text.
  let exportMarkets = allMarkets
  let previewLinks: PreviewLink[] | undefined
  let marketAudience = emptyMarketAudience
  if (isMultiMarket) {
    try {
      const { data } = await client.query<MarketAudiencesData>({
        context: MARKET_AUDIENCES_CONTEXT,
        query: GET_MARKET_AUDIENCES,
        variables: { surveyId: docSurveyId },
        fetchPolicy: 'no-cache'
      })
      marketAudience = toMarketAudience(data, docSurveyId, allMarkets)
    } catch (e) {
      if (throwOnError) throw e
      marketAudience = emptyMarketAudience
    }

    const marketMetaEntries = await Promise.all(
      allMarkets.map(async (market): Promise<[string, MarketExportMeta]> => {
        try {
          const { data } = await client.query({
            context: { clientName: 'questionnaire' },
            query: GET_FIELDWORK,
            variables: { surveyId: market.surveyId }
          })
          const fw = data.fieldwork
          const languages = fieldworkLanguages(fw)
          const panel = fw?.audience[0]
            ? convertAudienceType(fw.audience[0].__typename)
            : PanelSupplier.CUSTOM
          return [market.surveyId, { languages, panel }]
        } catch (e) {
          if (throwOnError) throw e
          return [
            market.surveyId,
            { languages: [], panel: PanelSupplier.CUSTOM }
          ]
        }
      })
    )
    const marketMeta = new Map(marketMetaEntries)

    const scopedMarkets = market
      ? [market]
      : computeExportMarkets(allMarkets, marketMeta, languageCode)
    if (languageCode && scopedMarkets.length === 0 && allMarkets.length > 0) {
      datadogLogs.logger.warn(
        'Survey export: no market uses the selected language; falling back to all markets',
        { languageCode }
      )
    }
    exportMarkets = scopedMarkets.length > 0 ? scopedMarkets : allMarkets

    previewLinks = exportMarkets.map((market) => ({
      label: formatMarketLine(market),
      url: createPreviewUrl(
        market.surveyId,
        marketMeta.get(market.surveyId)?.panel ?? PanelSupplier.CUSTOM
      )
    }))
  }

  let responseOptionsByQuestion: ResponseOptionsByQuestion = {}

  // Custom audience translations are stored on `DraftCustomAudience` and
  // reached through GET_AUDIENCES. The questionnaire query's
  // `audienceEntries[].entryItem` projection used to carry
  // `translations: null`; it now carries them, but this stays the source of
  // truth for custom screeners. Fetch the
  // audience-side data here and use it to backfill the questionnaire view
  // before the overlay runs. Failures are swallowed so the export still
  // ships master text rather than blocking entirely.
  let audienceMembersById = new Map<string, FieldworkAudience>()
  try {
    const { data: audienceData } = await client.query<AudienceData>({
      context: { clientName: 'questionnaire' },
      query: GET_AUDIENCES,
      variables: { projectId, surveyId: docSurveyId, waveId },
      fetchPolicy: 'no-cache'
    })
    audienceMembersById = new Map(
      audienceData.draftAudience.members.map((m) => [m.id, m])
    )
  } catch (e) {
    if (throwOnError) throw e
    audienceMembersById = new Map()
  }

  // Copy translations from the audience-side `DraftCustomAudience` (and
  // its `criterionOptions[]`) into the matching questionnaire-side
  // audience entry so the existing `applyTranslationOverlay` can resolve
  // them like it does for survey entries. Translations now live on
  // `question.translations` / `responseOption.translations` on both sides,
  // so the synthesizer reads from and writes to those inner paths.
  const mergeAudienceTranslations = (
    entry: DraftQuestionnaireEntry
  ): DraftQuestionnaireEntry => {
    const member = audienceMembersById.get(entry.id)
    if (!member || member.memberType !== FieldworkAudienceType.Custom) {
      return entry
    }
    const audienceItem = member.memberItem as DraftCustomAudience
    const item = entry.entryItem as DraftQuestionItem
    const optionTranslations = new Map<
      string,
      Pick<AudienceResponseOption, 'translations' | 'localeTranslations'>
    >()
    audienceItem.criterionOptions.forEach((opt) => {
      const { translations, localeTranslations } = opt.responseOption
      if (translations?.length || localeTranslations?.length) {
        optionTranslations.set(opt.responseOption.responseOptionId, {
          translations:
            translations?.map((t) => ({
              language: t.language,
              value: t.value,
              __typename: 'ResponseOptionTranslation' as const
            })) ?? null,
          localeTranslations: localeTranslations ?? null
        })
      }
    })
    return {
      ...entry,
      entryItem: {
        ...item,
        question: item.question
          ? {
              ...item.question,
              translations:
                audienceItem.question.translations?.map((t) => ({
                  language: t.language,
                  text: t.text,
                  textStyling: t.textStyling ?? null,
                  __typename: 'QuestionTranslation' as const
                })) ?? null,
              localeTranslations:
                audienceItem.question.localeTranslations ?? null
            }
          : item.question,
        responseOptions: item.responseOptions.map((opt) => {
          const optId = opt.responseOption?.responseOptionId
          const translations = optId ? optionTranslations.get(optId) : undefined
          if (!translations || !opt.responseOption) return opt
          return {
            ...opt,
            responseOption: { ...opt.responseOption, ...translations }
          }
        })
      }
    } as DraftQuestionnaireEntry
  }

  // try to get questionnaire entries
  try {
    const { data: questionnaireData } = await client.query<QuestionnaireData>({
      context: { clientName: 'questionnaire' },
      query: QUESTIONNAIRE,
      // No `languageCode` here on purpose: the response shape is the
      // same regardless (master text + the `translations[]` array on
      // every entry), and we resolve the per-language view client-side
      // via `applyTranslationOverlay`. Passing the language argument
      // would double-process the data (server overlay + our overlay)
      // and force a separate cache slice for every language.
      variables: { projectId, surveyId: docSurveyId, waveId },
      fetchPolicy: 'no-cache'
    })

    const { draftQuestionnaire } = questionnaireData

    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (draftQuestionnaire) {
      // Country overrides first, so the overlay reads the market's own rows
      // (and an English override reaches the master text the English doc shows).
      const overlay = (
        entries: ReadonlyArray<DraftQuestionnaireEntry>,
        language: string | undefined
      ): DraftQuestionnaireEntry[] =>
        entries.map((e) => {
          const resolved = resolveLocaleOverrides(e, localeCountry)
          return language
            ? applyTranslationOverlay(resolved, language, {
                fallbackToMaster: true
              })
            : resolved
        })
      questionnaireEntries = overlay(
        draftQuestionnaire.entries,
        overlayLanguage
      )
      // For audience entries we first merge translations from the
      // audience-side query (historically the questionnaire response left
      // audience translations null; it now carries them, but the audience
      // query stays the source for custom screeners), then run the
      // same overlay so the doc builder sees translated screener text.
      questionnaireAudienceEntries = overlay(
        draftQuestionnaire.audienceEntries.map(mergeAudienceTranslations),
        audienceOverlayLanguage
      )
      // Logic and masking lines name answers through this map, so it must hold
      // the same wording the doc prints under each question.
      responseOptionsByQuestion = getResponseOptionsByQuestion(
        flattenAllEntries({
          ...draftQuestionnaire,
          entries: questionnaireEntries,
          audienceEntries: questionnaireAudienceEntries
        })
      )
    }
  } catch (e) {
    if (throwOnError) throw e
    // leave questionnaireEntries unchanged
  }

  return {
    projectName,
    languageCode,
    surveyId: docSurveyId,
    countryName,
    previewUrl: createPreviewUrl(docSurveyId, panelSupplierCode),
    previewLinks,
    questionnaireEntries,
    questionnaireAudienceEntries,
    marketAudience,
    responseOptionsByQuestion,
    includeAudienceQuestions,
    isMultiMarket,
    allMarkets: exportMarkets,
    market
  }
}
